import { Component, OnInit, Optional, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppSettings } from 'src/app/app-settings';
import { BondsServices } from 'src/app/services/bonds.service';
import { ShoppingCartService } from 'src/app/services/shopping-cart.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { EventosService } from 'src/app/services/eventos.service';
import { SeatsPanelComponent } from '../../bloques/seats-panel/seats-panel.component';
import { InfoCardComponent } from '../../shared/info-card/info-card.component';
import { LoginComponent } from '../../shared/login/login.component';
import { PassSeat, SingleSeatPassService } from 'src/app/services/single-seat-pass.service';
import { fixedPassFromLabel, fixedPassTicket, isFixedPricePass } from 'src/app/utils/fixed-price-pass.util';
import { isPassEventFinished, passUnavailableMessage } from 'src/app/utils/pass-availability.util';

interface EventSelection {
  eventId: number;
  sessionId: number;
  sessionDate: string;
  ticketId: number;
  ticketName: string;
  ticketPrice: number;
  seats?: { row: any; seat: any; section?: string | null }[];
  eventTitle?: string;
  eventImage?: string;
  cartLines?: any[];
}

@Component({
  selector: 'app-pass-purchase',
  templateUrl: './pass-purchase.component.html',
  styleUrls: ['./pass-purchase.component.scss']
})
export class PassPurchaseComponent implements OnInit {

  pass: any = null;
  loaded = false;
  notFound = false;
  // Por qué el abono ya no se puede completar (null si todavía se puede)
  unavailableMessage: string | null = null;
  submitting = false;

  passEvents: any[] = [];
  selectedEventId: number | null = null;
  allowedSessionsByEvent: Map<number, number[] | null> = new Map();

  centerEvent: any = null;
  loadingCenter = false;
  futureSessions: any[] = [];

  selectedSession: any = null;
  selectedTicket: any = null;
  openingSeatPanel = false;

  eventSelections: Map<number, EventSelection> = new Map();

  quantity: number = 1;
  fromEventId: number | null = null;

  // Compra en butaca única: `quantity` butacas, las mismas en todos los
  // eventos; el back devuelve el desglose y resuelve la entrada de cada uno.
  // Es obligatoria si el abono trae `single_seat` (no hay recorrido evento a
  // evento) y, en el resto de abonos de eventos específicos, se ofrece cuando
  // la butaca elegida en el primer evento está libre en todos.
  passSeats: PassSeat[] = [];
  passQuote: any = null;
  loadingQuote = false;
  // El comprador ha aceptado llevarse la misma butaca a todos los eventos
  sameSeatChosen = false;
  // Evento en cuyo mapa se eligieron `passSeats` (null = mapa del abono)
  passSeatsEventId: number | null = null;

  urlImages: string = AppSettings.MEDIA_URI + '/events_images/';

  constructor(
    @Optional() private dialogRef: MatDialogRef<PassPurchaseComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { passId: number; quantity?: number; fromEventId?: number },
    private router: Router,
    private bondsService: BondsServices,
    private cartService: ShoppingCartService,
    private auth: AuthenticationService,
    private eventService: EventosService,
    private dialog: MatDialog,
    private singleSeatPassService: SingleSeatPassService
  ) {}

  ngOnInit(): void {
    this.quantity = this.data?.quantity || 1;
    this.fromEventId = this.data?.fromEventId || null;
    const passId = this.data?.passId;
    if (passId) {
      this.loadPass(passId);
    } else {
      this.notFound = true;
      this.loaded = true;
    }
  }

  loadPass(id: number) {
    this.bondsService.getPassById(id).subscribe({
      next: (res) => {
        this.pass = res?.discount || res?.data || res;
        if (!this.pass || !this.pass.active) {
          this.notFound = true;
          this.loaded = true;
          return;
        }
        // Only show web-compatible passes ([1,4,5])
        const allowedChannels: number[] = this.pass.allowed_channels || [];
        if (allowedChannels.length > 0 && !allowedChannels.some((c: number) => [1, 4, 5].includes(c))) {
          this.notFound = true;
          this.loaded = true;
          return;
        }
        const itemsByEvent = new Map<number, any[]>();
        for (const item of (this.pass.pass_items || [])) {
          if (!item.event) continue;
          const eid: number = item.event_id ?? item.event?.id;
          if (!itemsByEvent.has(eid)) itemsByEvent.set(eid, []);
          itemsByEvent.get(eid)!.push(item);
        }
        this.allowedSessionsByEvent = new Map();
        itemsByEvent.forEach((items, eventId) => {
          const hasNull = items.some((i: any) => !i.session_id);
          this.allowedSessionsByEvent.set(eventId, hasNull ? null : items.map((i: any) => i.session_id));
        });
        this.passEvents = Array.from(itemsByEvent.values()).map(items => {
          const event = items[0].event;
          // Fecha de su sesión, para la lista informativa de la butaca única
          const sessionDate = items[0]?.session?.start_date || event.start_date;
          return { ...event, _isFinished: isPassEventFinished(event, items), _sessionDate: sessionDate };
        });
        this.unavailableMessage = passUnavailableMessage(this.pass);
        this.loaded = true;
        if (this.singleSeatRequired) {
          // Los eventos son solo informativos: se listan por fecha de su sesión
          this.passEvents = [...this.passEvents]
            .sort((a, b) => new Date(a._sessionDate).getTime() - new Date(b._sessionDate).getTime());
          // Directo al mapa: el asistente solo se ve con la butaca ya elegida
          this.openPassSeatMap();
          return;
        }
        if (this.unavailableMessage) return;
        const startEvent = this.fromEventId
          ? this.passEvents.find(e => e.id === this.fromEventId && !this.isEventUnavailable(e))
          : null;
        const firstAvailable = this.passEvents.find(e => !this.isEventUnavailable(e));
        this.selectEvent(startEvent || firstAvailable);
      },
      error: () => {
        this.notFound = true;
        this.loaded = true;
      }
    });
  }

  // --- Event selection ---

  isEventUnavailable(event: any): boolean {
    return !!event?._isFinished || !!event?.only_tpv_sales || event?.active === false;
  }

  selectEvent(event: any) {
    if (!event || this.isEventUnavailable(event) || this.unavailableMessage) return;
    this.selectedEventId = event.id;
    this.selectedSession = null;
    this.selectedTicket = null;
    this.loadCenterEvent(event.id);
  }

  loadCenterEvent(eventId: number) {
    this.loadingCenter = true;
    this.centerEvent = null;
    this.futureSessions = [];

    this.eventService.getEventById(eventId).subscribe({
      next: (res) => {
        this.centerEvent = res.data || res;
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const allowedSessions = this.allowedSessionsByEvent.get(eventId) ?? null;
        this.futureSessions = (this.centerEvent.sessions || [])
          .filter((s: any) => {
            if (new Date(s.start_date) < now) return false;
            if (allowedSessions === null) return true;
            return allowedSessions.includes(s.id);
          })
          .sort((a: any, b: any) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());

        const eventInList = this.passEvents.find(e => e.id === eventId);
        if (eventInList) {
          eventInList._isFinished = this.futureSessions.length === 0;
        }

        if (this.eventSelections.has(eventId)) {
          const sel = this.eventSelections.get(eventId)!;
          this.selectedSession = this.futureSessions.find(s => s.id === sel.sessionId) || null;
          if (this.selectedSession && !this.hasMap(this.centerEvent)) {
            this.selectedTicket = (this.centerEvent.tickets || []).find((t: any) => t.id === sel.ticketId) || null;
          }
        } else if (this.futureSessions.length === 1) {
          this.selectedSession = this.futureSessions[0];
          const tickets = this.getAvailableTickets();
          if (!this.hasMap(this.centerEvent) && tickets.length === 1) {
            this.selectedTicket = tickets[0];
          }
        }
        this.loadingCenter = false;
      },
      error: () => {
        this.loadingCenter = false;
      }
    });
  }

  // --- Center panel ---

  hasMap(event: any): boolean {
    return event?.use_map === true || event?.id_map != null;
  }

  getAvailableTickets(): any[] {
    if (!this.centerEvent?.tickets) return [];
    const tickets = this.centerEvent.tickets.filter((t: any) => t.active && !t.only_tpv_sales);
    // Precio fijo sin mapa: una sola entrada, la del abono, a su precio
    if (this.isFixedPrices) {
      const passTicket = this.selectedSession ? fixedPassTicket(this.pass, this.selectedSession.id, null) : null;
      return passTicket ? [passTicket] : [];
    }
    return tickets;
  }

  /** Abono de precio fijo por evento y nivel (ver fixed-price-pass.util) */
  get isFixedPrices(): boolean {
    return isFixedPricePass(this.pass);
  }

  /** Lo que cuesta lo elegido en un evento: en mapa ya viene sumado por butaca */
  getSelectionTotal(sel: EventSelection | undefined): number {
    if (!sel) return 0;
    return sel.cartLines?.length ? sel.ticketPrice : sel.ticketPrice * this.quantity;
  }

  /** Total de lo elegido hasta ahora; con precio fijo es lo que costará el abono */
  get selectionsTotal(): number {
    return Array.from(this.eventSelections.values()).reduce((sum, sel) => sum + this.getSelectionTotal(sel), 0);
  }

  selectSession(session: any) {
    this.selectedSession = session;
    this.selectedTicket = null;
    if (!this.hasMap(this.centerEvent)) {
      const tickets = this.getAvailableTickets();
      if (tickets.length === 1) this.selectedTicket = tickets[0];
    }
  }

  selectTicket(ticket: any) {
    this.selectedTicket = ticket;
  }

  /**
   * ¿Hay que pedir login antes de seguir? En el tótem nunca: la compra cuelga
   * del usuario del kiosco.
   */
  private needsLoginToBuy(): boolean {
    if (AppSettings.ENV_TOTEM) {
      return false;
    }
    return !this.auth.getProfile();
  }

  /**
   * Pide login (o registro) en un diálogo y, si lo consigue, retoma la acción
   * que se quedó a medias. El asistente se queda abierto por debajo, así que
   * no se pierde lo que ya hubiera elegido para otros eventos del abono.
   */
  private requestLogin(onLogged: () => void, onCancelled?: () => void) {
    const loginRef = this.dialog.open(LoginComponent, {
      panelClass: ['info-dialog', 'dialog-login'],
      data: {
        exit: false,
        seats: true
      }
    });
    loginRef.afterClosed().subscribe((res: any) => {
      if (res?.login) {
        onLogged();
      } else {
        onCancelled?.();
      }
    });
  }

  confirmTicketSelection() {
    if (!this.selectedSession || !this.selectedTicket || !this.centerEvent) return;
    // La cuenta se pide al elegir la entrada, no al final: si no, el usuario
    // recorre todos los eventos del abono y se topa con el login cuando ya lo
    // tiene todo hecho.
    if (this.needsLoginToBuy()) {
      this.requestLogin(() => this.confirmTicketSelection());
      return;
    }
    this.saveSelection({
      eventId: this.centerEvent.id,
      sessionId: this.selectedSession.id,
      sessionDate: this.selectedSession.start_date,
      ticketId: this.selectedTicket.id,
      ticketName: this.selectedTicket.name,
      ticketPrice: +this.selectedTicket.price,
      eventTitle: this.centerEvent.title,
      eventImage: this.centerEvent.image_url,
    });
  }

  openSeatSelector() {
    if (!this.selectedSession || !this.centerEvent || this.openingSeatPanel) return;
    // Igual que en la compra de entradas: el login se pide antes de abrir el
    // mapa, no después de haber elegido butaca.
    if (this.needsLoginToBuy()) {
      this.requestLogin(() => this.openSeatSelector());
      return;
    }
    this.openingSeatPanel = true;
    const event = this.centerEvent;
    const session = this.selectedSession;

    this.eventService.getMapEventBySession(event.id, session.id).subscribe({
      next: (res: any) => {
        this.openingSeatPanel = false;
        const tickets = this.getAvailableTickets().filter((t: any) =>
          !t.id_session || t.id_session == session.id
        );
        const seatDialog = this.dialog.open(SeatsPanelComponent, {
          panelClass: ['info-dialog', 'not-padding', 'panel-seats'],
          data: { event, session, mapEvent: res.data, tickets, fromAbono: true, abono: this.pass, abonoQuantity: this.quantity }
        });
        seatDialog.afterClosed().subscribe((data: any) => {
          if (data?.cart_lines?.length > 0) {
            const seats = data.seats || [];
            const selection: EventSelection = {
              eventId: event.id,
              sessionId: session.id,
              sessionDate: session.start_date,
              ticketId: data.cart_lines[0].id_ticket,
              ticketName: this.isFixedPrices ? (this.pass.public_name || this.pass.name) : 'Entrada',
              // Con precio fijo el panel ya suma el precio del abono de cada butaca
              ticketPrice: this.isFixedPrices ? +data.price || 0 : 0,
              cartLines: data.cart_lines,
              seats: seats.map((s: any) => ({ row: s.row, seat: s.seat, section: s.section?.name || null })),
              eventTitle: event.title,
              eventImage: event.image_url,
            };
            this.offerSameSeatOrSave(selection, seats);
          }
        });
      },
      error: (err: any) => {
        this.openingSeatPanel = false;
        if (err.status !== 404) {
          this.openInfoCard('Error al cargar el mapa del evento. Por favor, inténtalo de nuevo.');
        } else {
          this.centerEvent = { ...this.centerEvent, use_map: false, id_map: null };
        }
      }
    });
  }

  /**
   * Tras elegir butaca en el primer evento: si está libre en todos los del
   * abono se ofrece llevársela a todos, y si el comprador acepta se pasa a la
   * compra en butaca única. En cualquier otro caso la selección se guarda y el
   * recorrido evento a evento sigue como siempre.
   */
  private offerSameSeatOrSave(selection: EventSelection, seats: any[]) {
    const otherEventsChosen = Array.from(this.eventSelections.keys()).some(id => id !== selection.eventId);
    const canOffer = this.singleSeatPassService.canOffer(this.pass)
      && !otherEventsChosen
      && this.passEvents.length > 1
      && seats.length === this.quantity
      && this.passEvents.every(e => this.hasMap(e) && !this.isEventUnavailable(e));

    if (!canOffer) {
      this.saveSelection(selection);
      return;
    }

    this.loadingCenter = true;
    this.singleSeatPassService.offerSameSeat(this.pass, selection.eventId, seats.map((s: any) => s.id)).subscribe((quote) => {
      this.loadingCenter = false;
      if (!quote) {
        this.saveSelection(selection);
        return;
      }
      this.passSeats = seats.map((s: any) => ({ id: s.id, row: s.row, seat: s.seat, section: s.section?.name || null }));
      this.passSeatsEventId = selection.eventId;
      this.passQuote = quote;
      this.sameSeatChosen = true;
    });
  }

  /** Deshace la butaca única ofrecida y vuelve al recorrido evento a evento. */
  backToEventByEvent() {
    this.sameSeatChosen = false;
    this.passSeats = [];
    this.passSeatsEventId = null;
    this.passQuote = null;
    this.eventSelections = new Map();
    this.selectEvent(this.passEvents.find(e => !this.isEventUnavailable(e)));
  }

  private saveSelection(sel: EventSelection) {
    this.eventSelections.set(sel.eventId, sel);
    this.eventSelections = new Map(this.eventSelections);
    if (!this.allEventsCompleted) {
      const next = this.passEvents.find(e => !this.eventSelections.has(e.id) && !this.isEventUnavailable(e));
      if (next) setTimeout(() => this.selectEvent(next), 350);
    }
  }

  removeSelection(eventId: number) {
    this.eventSelections.delete(eventId);
    this.eventSelections = new Map(this.eventSelections);
    if (this.selectedEventId === eventId) {
      this.selectedSession = null;
      this.selectedTicket = null;
    }
  }

  // --- Progress ---

  get minEvents(): number {
    return this.pass?.min_events || this.passEvents.length;
  }

  get isFlexible(): boolean {
    return this.pass?.min_events && this.pass.min_events < this.passEvents.length;
  }

  get completedCount(): number {
    return this.eventSelections.size;
  }

  get allEventsCompleted(): boolean {
    return this.completedCount >= this.minEvents;
  }

  get progressPercent(): number {
    if (this.minEvents === 0) return 0;
    return Math.min(100, Math.round((this.completedCount / this.minEvents) * 100));
  }

  isEventCompleted(eventId: number): boolean {
    return this.eventSelections.has(eventId);
  }

  isCurrentEvent(eventId: number): boolean {
    return this.selectedEventId === eventId;
  }

  getSelectionForEvent(eventId: number): EventSelection | undefined {
    return this.eventSelections.get(eventId);
  }

  getDiscountBadge(): string {
    if (!this.pass) return '';
    if (this.isFixedPrices) return fixedPassFromLabel(this.pass);
    if (this.pass.discount_mode === 'percentage') return `${Math.trunc(this.pass.discount_value)}% descuento`;
    if (this.pass.discount_mode === 'fixed') return `-${this.pass.discount_value}€`;
    return '';
  }

  // --- Cart ---

  addToCart() {
    if (!this.allEventsCompleted || this.submitting) return;
    // Red de seguridad por si la sesión caduca a mitad del asistente: se pide
    // login en un diálogo encima y se reintenta, en vez de cerrar el asistente
    // y mandarlo a /login perdiendo toda la selección.
    if (this.needsLoginToBuy()) {
      this.requestLogin(() => this.addToCart());
      return;
    }
    this.submitting = true;

    const cartLines: any[] = [];
    Array.from(this.eventSelections.values()).forEach(sel => {
      if (sel.cartLines?.length) {
        sel.cartLines.forEach((cl: any) => {
          cartLines.push({ ...cl, id_event: sel.eventId, id_session: sel.sessionId, id_discount: this.pass.id });
        });
      } else {
        cartLines.push({
          id_event: sel.eventId,
          id_session: sel.sessionId,
          id_ticket: sel.ticketId,
          quantity: this.quantity,
          id_discount: this.pass.id,
        });
      }
    });

    this.cartService.addBondToCard({ id_discount: this.pass.id, cart_lines: cartLines }).subscribe({
      next: () => {
        this.submitting = false;
        this.cartService.setModifyCart(true);
        this.dialogRef?.close({ added: true });
        this.router.navigate(['/cart-checkout']);
      },
      error: (err: any) => {
        this.submitting = false;
        const msg = err.error?.message || err.error?.errors?.errors?.[0] || 'Ha ocurrido un error al añadir el abono al carrito.';
        this.openInfoCard(msg);
      }
    });
  }

  // --- Abono de butaca única ---

  /** El abono solo se vende en butaca única */
  get singleSeatRequired(): boolean {
    return !!this.pass?.single_seat;
  }

  /** La compra en curso es en butaca única: obligatoria, o aceptada al ofrecérsela */
  get isSingleSeat(): boolean {
    return this.singleSeatRequired || this.sameSeatChosen;
  }

  /**
   * Igual que en un abono de eventos específicos normal: si alguno de sus
   * eventos ya no se puede comprar, el abono entero tampoco.
   */
  get singleSeatBlocked(): boolean {
    return this.passEvents.some(e => this.isEventUnavailable(e));
  }

  /** Butaca única obligatoria aún sin butaca: el comprador está en el mapa, no en el asistente. */
  get awaitingSeats(): boolean {
    return this.singleSeatRequired && !this.notFound && !this.singleSeatBlocked && !this.passSeats.length;
  }

  /** Sin butaca no hay nada que enseñar en el asistente: se cierra todo. */
  private closeIfAwaitingSeats() {
    if (this.awaitingSeats) {
      this.close();
    }
  }

  openPassSeatMap() {
    if (this.openingSeatPanel || this.singleSeatBlocked) return;
    // Igual que en la compra de entradas: el login se pide antes de abrir el
    // mapa, no después de haber elegido butaca.
    if (this.needsLoginToBuy()) {
      this.requestLogin(() => this.openPassSeatMap(), () => this.closeIfAwaitingSeats());
      return;
    }
    this.openingSeatPanel = true;
    const hideDialog = this.awaitingSeats;
    this.singleSeatPassService.pickSeats(this.pass, this.quantity, () => {
      if (hideDialog) this.dialogRef?.addPanelClass('dialog-behind-map');
    }).subscribe({
      next: (seats) => {
        this.openingSeatPanel = false;
        this.dialogRef?.removePanelClass('dialog-behind-map');
        if (seats) {
          this.passSeats = seats;
          this.passSeatsEventId = null;
          this.loadPassQuote();
        } else {
          this.closeIfAwaitingSeats();
        }
      },
      error: (err: any) => {
        this.openingSeatPanel = false;
        this.dialogRef?.removePanelClass('dialog-behind-map');
        this.openInfoCard(err.error?.message || 'Error al cargar el mapa del abono. Por favor, inténtalo de nuevo.');
        this.closeIfAwaitingSeats();
      }
    });
  }

  private loadPassQuote() {
    this.passQuote = null;
    this.loadingQuote = true;
    this.bondsService.quotePassSeats(this.pass.id, this.passSeats.map(s => s.id), this.passSeatsEventId).subscribe({
      next: (res: any) => {
        this.passQuote = res.data;
        this.loadingQuote = false;
      },
      error: (err: any) => {
        this.loadingQuote = false;
        this.onSingleSeatError(err);
      }
    });
  }

  addSingleSeatPassToCart() {
    if (!this.passQuote || this.submitting) return;
    if (this.needsLoginToBuy()) {
      this.requestLogin(() => this.addSingleSeatPassToCart());
      return;
    }
    this.submitting = true;

    this.cartService.addSingleSeatPass({
      id_discount: this.pass.id,
      seat_ids: this.passSeats.map(s => s.id),
      event_id: this.passSeatsEventId,
    }).subscribe({
      next: () => {
        this.submitting = false;
        this.cartService.setModifyCart(true);
        this.dialogRef?.close({ added: true });
        this.router.navigate(['/cart-checkout']);
      },
      error: (err: any) => {
        this.submitting = false;
        this.onSingleSeatError(err);
      }
    });
  }

  /**
   * Si otra persona se ha quedado alguna de las butacas entre medias, el back
   * no añade nada: se avisa, se descarta la selección y se vuelve a abrir el
   * mapa, que se pide de nuevo y ya sale actualizado.
   */
  private onSingleSeatError(err: any) {
    const seatsTaken = err.status === 409 && err.error?.errors?.code === 'seats_unavailable';
    if (!seatsTaken) {
      // El detalle va primero: `message` puede ser solo la clave genérica del carrito
      this.openInfoCard(err.error?.errors?.errors?.[0] || err.error?.message || 'Ha ocurrido un error al añadir el abono al carrito.');
      if (this.singleSeatRequired && !this.passQuote) {
        this.close();
      }
      return;
    }

    this.passSeats = [];
    this.passSeatsEventId = null;
    this.passQuote = null;
    const many = this.quantity > 1;
    this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: {
        icon: false,
        text: `${many ? 'Alguna de las butacas que habías elegido' : 'La butaca que habías elegido'} acaba de ocuparse en uno de los eventos del abono. Elige ${many ? 'de nuevo' : 'otra'} en el mapa.`,
        btnCancel: false
      }
    }).afterClosed().subscribe(() => this.openPassSeatMap());
  }

  close() {
    this.dialogRef?.close();
  }

  private openInfoCard(msg: string) {
    this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: { icon: false, text: msg, btnCancel: false }
    });
  }
}
